import React, { memo, useCallback } from 'react';

import Form from 'react-bootstrap/Form';

import _trim from 'lodash/trim';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';

import i18n from '../messages.json';

/**
 * Editable list of "one input per row" recipients (emails, user IDs, FCM tokens, …).
 *
 * Parent owns the list state and passes a single `onChange(nextList)` callback.
 * The list is never empty — when the last row is removed, an empty string is
 * preserved so the user always has something to type into.
 *
 * Props
 *   list:           string[] — current recipient values (parent state)
 *   onChange:       (next: string[]) => void
 *   inputType:      HTML input type ('email' | 'text' | …)
 *   placeholder:    Placeholder for each input
 *   validator:      (value: string) => boolean — used for the per-row invalid feedback
 *   invalidMessage: string — message shown when validator returns false
 */
const RecipientList = ({
  list,
  onChange,
  inputType,
  placeholder,
  validator,
  invalidMessage,
}) => {
  const setAt = useCallback((idx, value) => {
    const next = list.length > 0 ? [...list] : [''];

    next[idx] = value;
    onChange(next);
  }, [list, onChange]);

  const add = useCallback(() => {
    if (list[list.length - 1] === '') return;
    onChange([...list, '']);
  }, [list, onChange]);

  const remove = useCallback((idx) => {
    const next = list.filter((_v, i) => i !== idx);

    onChange(next.length === 0 ? [''] : next);
  }, [list, onChange]);

  return (
    <div className='send-test__list'>
      {list.map((value, idx) => {
        const trimmed = _trim(value);
        const invalid = trimmed.length > 0 && !validator(trimmed);

        return (
          <div className='send-test__row' key={idx}>
            <Form.Control
              type={inputType}
              value={value}
              placeholder={placeholder}
              isInvalid={invalid}
              onChange={(e) => setAt(idx, e.target.value)}
            />
            <button
              type='button'
              className='send-test__row-remove'
              aria-label={i18n.ink.notification.sendTest.removeRecipient}
              onClick={() => remove(idx)}
              disabled={list.length === 1 && trimmed === ''}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            {invalid && (
              <div className='send-test__row-error'>{invalidMessage}</div>
            )}
          </div>
        );
      })}

      <button
        type='button'
        className='send-test__add'
        onClick={add}
        disabled={list[list.length - 1] === ''}
      >
        <FontAwesomeIcon icon={faPlus} />
        <span>{i18n.ink.notification.sendTest.addAnother}</span>
      </button>
    </div>
  );
};

export default memo(RecipientList);
